import { useState, useEffect } from "react";
import { updatePriceInDB } from "../firebase/firebaseService";
import { FaTag, FaDollarSign, FaSave, FaCheckCircle } from "react-icons/fa";

type Service = {
  id: string;
  name: string;
  price: number;
};

interface PriceUpdateProps {
  precios: any[];
}

const PriceUpdater = ({ precios }: PriceUpdateProps) => {
  const [services, setServices]           = useState<Service[]>([]);
  const [editedServices, setEditedServices] = useState<Service[]>([]);
  const [isChanged, setIsChanged]         = useState(false);
  const [loading, setLoading]             = useState(false);
  const [saved, setSaved]                 = useState(false);

  useEffect(() => {
    if (precios.length > 0) {
      const updated = precios.map((p) => ({
        id:    p.id,
        name:  p.servicio,
        price: Number(p.costo),
      }));
      setServices(updated);
      setEditedServices(updated.map((s) => ({ ...s })));
    }
  }, [precios]);

  const handlePriceChange = (index: number, newPrice: number) => {
    const updated = editedServices.map((s, i) =>
      i === index ? { ...s, price: newPrice } : s
    );
    setEditedServices(updated);
    setIsChanged(true);
    setSaved(false);
  };

  const handleApplyChanges = async () => {
    setLoading(true);
    for (const service of editedServices) {
      await updatePriceInDB(service.id, service.price);
    }
    setServices(editedServices.map((s) => ({ ...s })));
    setIsChanged(false);
    setLoading(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const hasModified = (index: number) =>
    editedServices[index]?.price !== services[index]?.price;

  return (
    <div>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#3054A0] to-blue-500 px-8 py-8">
        <h1 className="text-3xl font-bold text-white">Actualización de Precios</h1>
        <p className="text-blue-200 text-sm mt-1">
          Ajusta los precios que se muestran en la página web de Gobies
        </p>

        <div className="mt-6 inline-flex items-center gap-3 bg-white/20 backdrop-blur rounded-2xl px-5 py-3">
          <div className="w-9 h-9 bg-white/30 rounded-xl flex items-center justify-center">
            <FaTag className="text-white" size={16} />
          </div>
          <div>
            <p className="text-blue-100 text-[13px] font-medium">Servicios configurados</p>
            <p className="text-white text-2xl font-bold">{services.length}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 lg:p-8">
        {services.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center h-48 text-gray-400">
            <div className="animate-spin w-8 h-8 border-4 border-blue-300 border-t-blue-600 rounded-full" />
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

            {/* Column headers — repeated for each of the 2 visual columns */}
            <div className="hidden sm:grid grid-cols-2 bg-gray-50 border-b border-gray-100">
              {[0, 1].map((col) => (
                <div key={col} className={`flex items-center justify-between px-5 py-3 ${col === 0 ? "border-r border-gray-100" : ""}`}>
                  <span className="text-[13px] font-semibold text-gray-500 uppercase tracking-wider">Servicio</span>
                  <span className="text-[13px] font-semibold text-gray-500 uppercase tracking-wider">Precio</span>
                </div>
              ))}
            </div>
            {/* Mobile header */}
            <div className="sm:hidden flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-100">
              <span className="text-[13px] font-semibold text-gray-500 uppercase tracking-wider">Servicio</span>
              <span className="text-[13px] font-semibold text-gray-500 uppercase tracking-wider">Precio</span>
            </div>

            {/* 2-column grid of rows */}
            <div className="grid grid-cols-1 sm:grid-cols-2">
              {services.map((service, index) => {
                const modified = hasModified(index);
                // Add right border to left column items (odd indices 0,2,4…)
                const isLeft = index % 2 === 0;
                return (
                  <div
                    key={service.id}
                    className={`flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-50 transition-colors ${
                      isLeft ? "sm:border-r sm:border-gray-100" : ""
                    } ${modified ? "bg-blue-50/60" : "hover:bg-gray-50/60"}`}
                  >
                    {/* Nombre */}
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${
                        modified ? "bg-blue-100" : "bg-gray-100"
                      }`}>
                        <FaTag size={12} className={modified ? "text-blue-500" : "text-gray-400"} />
                      </div>
                      <div className="min-w-0">
                        <span className={`text-[15px] font-semibold leading-tight block truncate ${
                          modified ? "text-blue-800" : "text-gray-700"
                        }`}>
                          {service.name}
                        </span>
                        {modified && (
                          <span className="text-[11px] font-bold text-blue-500">
                            Modificado
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Input precio */}
                    <div className={`flex items-center border rounded-xl overflow-hidden shrink-0 transition-all ${
                      modified
                        ? "border-blue-400 ring-2 ring-blue-100"
                        : "border-gray-200 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100"
                    }`}>
                      <span className={`px-2.5 py-2.5 text-[15px] font-bold border-r ${
                        modified
                          ? "bg-blue-100 text-blue-600 border-blue-300"
                          : "bg-gray-50 text-gray-400 border-gray-200"
                      }`}>
                        $
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editedServices[index]?.price ?? ""}
                        onChange={(e) => handlePriceChange(index, Number(e.target.value))}
                        className="w-20 px-2.5 py-2.5 text-[15px] font-semibold text-gray-800 bg-white focus:outline-none text-right"
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer con botón */}
            <div className="px-6 py-5 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-4">
              <p className="text-[13px] text-gray-400">
                {isChanged
                  ? `${editedServices.filter((s, i) => s.price !== services[i]?.price).length} precio(s) sin guardar`
                  : saved
                  ? "✓ Todos los cambios guardados"
                  : "Modifica los precios y guarda los cambios"}
              </p>

              <button
                onClick={handleApplyChanges}
                disabled={!isChanged || loading}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-[15px] transition-all ${
                  saved
                    ? "bg-emerald-100 text-emerald-700 cursor-default"
                    : isChanged
                    ? "bg-gradient-to-r from-[#3054A0] to-blue-500 text-white shadow-lg shadow-blue-200 hover:from-blue-700 hover:to-blue-600"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Guardando...
                  </>
                ) : saved ? (
                  <>
                    <FaCheckCircle size={14} />
                    Guardado
                  </>
                ) : (
                  <>
                    <FaSave size={14} />
                    Guardar Cambios
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Overlay de carga */}
      {loading && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            <p className="text-gray-700 font-semibold text-[15px]">Guardando precios...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceUpdater;
